import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Navigation Component
const Navigation = () => {
  const location = useLocation();
  
  const navItems = [
    { name: 'Home', shortName: 'Home', path: '/', icon: '◉', premium: true },
    { name: 'Categories', shortName: 'Categories', path: '/categories', icon: '▣', premium: true },
    { name: 'Featured Videos', shortName: 'Featured', path: '/featured', icon: '◆', premium: true },
    { name: 'Latest Uploads', shortName: 'Latest', path: '/latest', icon: '▲', premium: true }
  ];

  return (
    <div className="nav-menu">
      <div className="container mx-auto px-2 md:px-6">
        <nav className="flex items-center justify-center space-x-1 md:space-x-2 py-4">
          {navItems.map((item) => (
            <Button
              key={item.name}
              onClick={() => window.location.href = item.path}
              variant="ghost"
              className={`nav-link px-1 md:px-6 py-2 md:py-3 rounded-lg transition-all duration-300 text-xs md:text-base ${
                location.pathname === item.path ? 'active' : ''
              } ${item.premium ? 'premium-nav-btn' : ''}`}
              data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}-btn`}
            >
              <div className="flex items-center space-x-1 md:space-x-3">
                <span className="text-orange-400 font-bold text-sm md:text-xl premium-nav-icon">{item.icon}</span>
                <span className="font-medium whitespace-nowrap text-xs md:text-base">
                  <span className="block md:hidden">{item.shortName}</span>
                  <span className="hidden md:block">{item.name}</span>
                </span>
              </div>
            </Button>
          ))}
        </nav>
      </div>
    </div>
  );
};

// Header Component
const Header = ({ searchTerm, onSearchChange, onCategoryClick, categories, showNavigation = true }) => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`premium-sticky-header ${isScrolled ? 'scrolled' : ''}`}>
      <div className={`container mx-auto px-6 header-content ${isScrolled ? 'py-3' : 'py-4'}`}>
        {/* Logo, Search Bar, and Language Layout */}
        <div className={`flex items-center justify-between space-x-6 ${isScrolled ? 'mb-3' : 'mb-6'}`}>
          {/* Logo Section - Left Side */}
          <div className="flex items-center space-x-3">
            <div className="text-3xl font-bold text-white cursor-pointer hover:scale-105 transition-transform" onClick={() => window.location.href = '/'}>
              <span className="text-orange-400 font-black text-4xl">X</span><span className="text-white">playvide</span>
            </div>
            <Badge className="bg-orange-600/20 text-orange-400 border-orange-500/50 animate-glow text-lg px-4 py-2">
              Premium HD
            </Badge>
          </div>

          {/* Search Bar - Center */}
          <div className="relative flex-1 max-w-2xl">
            <Input
              type="text"
              placeholder="Search thousands of premium videos..."
              value={searchTerm || ''}
              onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
              className="search-input w-full h-14 pl-14 pr-4 text-white placeholder-gray-400 text-lg"
              data-testid="search-input"
            />
            <div className="absolute left-5 top-1/2 transform -translate-y-1/2 text-orange-400">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          
          {/* Language Selector - Right Side (English Only) */}
          <div className="flex items-center space-x-2 bg-gray-800/50 rounded-lg px-4 py-3 border border-gray-600">
            <div className="text-orange-400 text-xl">🌐</div>
            <span className="text-white text-sm font-medium">English</span>
          </div>
        </div>

        {/* Categories section removed as requested */}
      </div>
      
      {/* Navigation Menu */}
      {showNavigation && <Navigation />}
    </header>
  );
};

export default Header;